/**
 * Newsletter entry helpers (2026-10-19): featured vs regular layout and
 * "Add from link" entries. Mirrors `resolve_displays` in
 * backend/newsletter_template.py so the builder shows the same style
 * the email will use.
 *
 * Pure and framework-free — node --test frontend/scripts/newsletterEntries.test.mjs
 */

/** The composed newsletter as it goes out: the subject and preheader the
 *  editor may have changed in the preview, without stray spaces. */
export function withEditedCopy(composed) {
  return {
    ...composed,
    subject: String(composed?.subject || "").trim(),
    preheader: String(composed?.preheader || "").trim(),
  };
}

/** True for a post added with "Add from link" (not a Newsdesk article). */
export function isLinkEntry(e) {
  return !!e && (e.source === "link" || String(e.article_id || "").startsWith("link:"));
}

/**
 * "featured" | "regular" for the entry at index `i`. An explicit
 * `display` wins; otherwise the first entry is featured when the
 * theme has a lead story.
 */
export function effectiveDisplay(entries, i, leadStory = true) {
  const d = entries?.[i]?.display;
  if (d === "featured" || d === "regular") return d;
  return i === 0 && leadStory ? "featured" : "regular";
}

/** Set an entry's style explicitly. */
export function setDisplay(entries, id, display) {
  return entries.map((e) => (e.article_id === id ? { ...e, display } : e));
}

/** Patch any fields of one entry. */
export function patchEntry(entries, id, patch) {
  return entries.map((e) => (e.article_id === id ? { ...e, ...patch } : e));
}

/**
 * After the picker's candidate list changes, keep entries still in it
 * — plus every link entry, which never comes from the picker. Returns
 * the same array when nothing dropped so React skips the re-render.
 */
export function keepValid(entries, candidates) {
  const valid = new Set((candidates || []).map((c) => c.article_id));
  const next = entries.filter((e) => isLinkEntry(e) || valid.has(e.article_id));
  return next.length === entries.length ? entries : next;
}

/**
 * The starting entry for a picked candidate. Digital editions go in
 * featured, with no teaser and no byline (heading + cover, like the
 * newsletters already sent), and skip the AI teaser.
 */
export function entryForCandidate(c) {
  const isEdition = c?.kind === "edition";
  return {
    article_id: c.article_id,
    source: "article",
    headline: c.headline,
    wordpress_url: c.wordpress_url,
    featured_image_url: c.featured_image_url,
    teaser: isEdition ? "" : (c.excerpt || "").slice(0, 220),
    ...(isEdition ? { display: "featured", show_byline: false } : {}),
  };
}

/** A link entry from POST /newsletter/link-entry, styled as chosen. */
export function entryFromLink(entry, display) {
  const featured = display === "featured";
  return {
    ...entry,
    source: "link",
    display: featured ? "featured" : "regular",
    show_byline: !featured,
  };
}

/** Would adding this entry duplicate one already in the newsletter? */
export function alreadyIn(entries, entry) {
  return (entries || []).some(
    (e) =>
      e.article_id === entry.article_id ||
      (!!entry.wordpress_url && e.wordpress_url === entry.wordpress_url),
  );
}
